import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { CalendarWidget } from '../ui/calendar-widget';
import { FileText, BellRing, Clock, AlertCircle, CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export function LegalDashboard() {
  // Mock data for "Pratiche Recenti"
  const recentCases = [
    { id: 1, title: "Ricorso Civile", client: "Giovanni Bianchi", date: "30 Apr 2026", status: "In Lavorazione", statusColor: "bg-amber-100 text-amber-700 border-amber-200" },
    { id: 2, title: "Contratto di Locazione", client: "Lucia Verdi", date: "25 Apr 2026", status: "Completata", statusColor: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    { id: 3, title: "Assistenza Legale", client: "Mario Esposito", date: "20 Apr 2026", status: "Chiuso", statusColor: "bg-red-100 text-red-700 border-red-200" },
  ];

  // Mock data for "Scadenze in Arrivo"
  const upcomingDeadlines = [
    { id: 1, title: "Consegna atti per udienza", dateLabel: "Oggi", dateColor: "bg-red-100 text-red-700 border-red-200", icon: AlertCircle, iconColor: "text-red-600" },
    { id: 2, title: "Deposito ricorso presso il Tribunale", dateLabel: "20 Apr", dateColor: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertCircle, iconColor: "text-amber-600" },
    { id: 3, title: "Scadenza pagamento onorario", dateLabel: "30 Apr", dateColor: "bg-slate-100 text-slate-700 border-slate-200", icon: Clock, iconColor: "text-slate-600" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column (Main Content) */}
      <div className="lg:col-span-2 space-y-6">
        
        {/* Top Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="font-medium">Pratiche Attive</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">8</span>
                  <span className="text-sm text-muted-foreground">questa settimana</span>
                </div>
              </div>
              <div className="bg-primary/10 p-3 rounded-full">
                <span className="font-bold text-primary">2</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <BellRing className="h-5 w-5 text-amber-600" />
                  <span className="font-medium">Scadenze Imminenti</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">5</span>
                  <span className="text-sm text-muted-foreground">oggi</span>
                </div>
              </div>
              <div className="bg-red-100 p-3 rounded-full text-red-600 font-bold">
                6
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pratiche Recenti Section 1 */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-2 rounded-md">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Pratiche Recenti</CardTitle>
            </div>
            <Button variant="outline" size="sm">Vedi Tutte</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-muted-foreground pb-2 border-b">
                <div className="col-span-5">Pratica</div>
                <div className="col-span-3">Cliente</div>
                <div className="col-span-2">Scadenza</div>
                <div className="col-span-2 text-right">Stato</div>
              </div>
              
              {recentCases.map((item) => (
                <div key={item.id} className="grid grid-cols-12 gap-4 items-center text-sm py-2 hover:bg-muted/50 rounded-lg transition-colors px-2 -mx-2">
                  <div className="col-span-5 flex items-center gap-3">
                    <div className="bg-orange-50 p-2 rounded text-orange-600">
                      <FileText className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{item.title}</span>
                  </div>
                  <div className="col-span-3 text-muted-foreground">{item.client}</div>
                  <div className="col-span-2">{item.date}</div>
                  <div className="col-span-2 flex justify-end">
                    <Badge variant="outline" className={`${item.statusColor} border-0`}>
                      {item.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pratiche Recenti Section 2 (Duplicate for layout filling as per request structure) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-2 rounded-md">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Pratiche Recenti</CardTitle>
            </div>
            <Button variant="outline" size="sm">Vedi Tutte</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentCases.map((item) => (
                <div key={item.id} className="flex items-center justify-between py-3 border-b last:border-0 hover:bg-muted/50 transition-colors px-2 -mx-2 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="bg-muted p-2 rounded-md">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="font-medium">{item.title}</div>
                      <div className="text-xs text-muted-foreground">{item.date}</div>
                    </div>
                  </div>
                  <Badge variant="outline" className={`${item.statusColor} border-0`}>
                    {item.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Right Column (Sidebar) */}
      <div className="space-y-6">
        
        {/* Calendar Widget */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Calendario Appuntamenti</CardTitle>
          </CardHeader>
          <CardContent>
            <CalendarWidget />
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <CardTitle className="text-lg">Scadenze in Arrivo</CardTitle>
            </div>
            <Button variant="ghost" size="sm" className="text-xs h-8">Vedi Tutte</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {upcomingDeadlines.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
                  <item.icon className={`h-5 w-5 mt-0.5 ${item.iconColor}`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium leading-tight mb-1">{item.title}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">in scadenze</p>
                      <Badge variant="secondary" className={`${item.dateColor} text-[10px] h-5 border-0`}>
                        {item.dateLabel}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}